import { useState, useCallback } from "react"
import { MovesHistory } from "@/types/types"
import { startFen } from "@/constants"
import axios from "axios"

const STORAGE_KEY = "chessdrop_guest_game"

type GuestGameState = {
  start: string
  moves: MovesHistory
  chess960: boolean
}

const emptyGame: GuestGameState = {
  start: startFen,
  moves: [],
  chess960: false,
}

function loadFromStorage(): GuestGameState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed && typeof parsed.start === "string" && Array.isArray(parsed.moves)) {
        return parsed
      }
    }
  } catch {
    // Corrupted storage — ignore
  }
  return emptyGame
}

export function useGuestGame() {
  const [gameState, setGameState] = useState<GuestGameState>(loadFromStorage)
  const [isSaving, setIsSaving] = useState(false)

  // Persistence callback — called by GameMovesProvider on every move change.
  // Writes to localStorage inside the state updater to keep hook state and storage in sync.
  const onMovesChange = useCallback((moves: MovesHistory) => {
    setGameState((prev) => {
      const next = { ...prev, moves }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      return next
    })
  }, [])

  const clearGame = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setGameState(emptyGame)
  }, [])

  const saveToServer = useCallback(async (liveMoves: MovesHistory): Promise<string | null> => {
    setIsSaving(true)
    try {
      const response = await axios.post("/api/games/create_single", {
        start: gameState.start,
        moves: liveMoves,
        chess960: gameState.chess960,
      })
      localStorage.removeItem(STORAGE_KEY)
      return response.data.id
    } catch (error) {
      console.error("Failed to save game", error)
      return null
    } finally {
      setIsSaving(false)
    }
  }, [gameState.start, gameState.chess960])

  return {
    initialGame: gameState,
    onMovesChange,
    clearGame,
    saveToServer,
    isSaving,
  }
}
